import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Download } from 'lucide-react';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost, LockTip } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel } from '../components/workflow/constants';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import { changesSinceReturn } from '../components/workflow/changeDiff';
import AttachmentsList from '../components/workflow/AttachmentsList';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { EngagementNameList, withCoverageFmt } from '../components/workflow/CoverageDisplay';
import NowName from '../components/shared/NowName';
import {
  qcAnswerGaps, qcSplitGaps, QC_TYPE_LABEL,
  expandAnswerChanges, qcAnswerLabels, engagementNameMap,
} from './qcQuestions';
import { QcFormView } from '../components/workflow/QcForm';
import QcReviewerPicks, {
  planFor, reviewerPicksOf, reviewerPickGaps, appendReviewerPicks,
} from '../components/workflow/QcReviewerPicks';
import WorkflowQcNew, { QcAnswersForm } from './WorkflowQcNew';
import { formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// QC6 / QC7 acceptance — detail page (docs/modules/workflow-qc.md).
// Form-only, covers engagements, CHOSEN-REVIEWER reviewed (the submitter
// names the reviewer(s) — one to three, in sequence, as the form's answers
// call for; the ApprovalsCard shows the stages). Approval writes the formal
// record onto the covered engagements.

// One definition of how this flow names and prints a changed field, shared by
// History and the "Changes since it was returned" card so the two can never
// disagree.
const QC_CHANGE_LABELS = {
  qcType: 'QC Type',
  engagements: 'Engagements Covered',
  entities: 'Companies Covered',
  clientGroup: 'Client Group',
  yearEnd: 'Financial Year End',
  remarks: 'Remarks',
  answers: 'QC6 / QC7 Form Answers',
  riskRatingChanged: 'Risk Rating Changed from Prior Year',
  reviewerName: 'Reviewer',
  additionalReviewerName: 'Second Reviewer',
  thirdReviewerName: 'Third Reviewer',
};
const fmtQcHistoryValue = withCoverageFmt(
  (field, v) => (v === null || v === undefined || v === '' ? '—' : String(v))
);

// QC_TYPE_LABEL 是「QC6 — Client Acceptance (new client)」這種完整句；晶片上
// 型別代號已經自己粗體站著，這裡只要它後半段的說明，免得寫成「QC6 QC6 — …」。
const qcTypeDescription = (qcType) => {
  const parts = (QC_TYPE_LABEL[qcType] || '').split('—');
  return parts.length > 1 ? parts.slice(1).join('—').trim() : '';
};

const WorkflowQcDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [review, setReview] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [engagementLock, setEngagementLock] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/qc/${id}`),
        fetchJSON(`/api/workflow/qc/${id}/events`),
      ]);
      setRequest(detail.request);
      setReview(detail.review || null);
      setCanReview(detail.canReview);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEngagementLock(detail.engagementLock || null);
      setIsDraft(!!detail.isDraft);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true); setError(null);
    try {
      const res = await fetchJSON(`/api/workflow/qc/${id}/approve`, { method: 'POST' });
      setNotice(res.request?.status === 'approved'
        ? (res.engagementsCompleted
          ? 'Approved — the QC record has been created and the engagement(s) are now completed.'
          : 'Approved — the QC record has been created and linked to the engagement(s).')
        : 'Your approval is recorded — the next reviewer has been notified.');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true); setError(null);
    try {
      await fetchJSON(`/api/workflow/qc/${id}/resubmit`, { method: 'POST' });
      setNotice('Re-submitted — the engagement partners have been notified. Recorded approvals were voided.');
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';

  // Per-question "what it said when you returned it" for the acceptance form.
  // The `answers` entry carries whole objects; keep them here rather than the
  // formatted rows the History table uses, so the form can look up by id.
  const answersWhenReturned = useMemo(() => {
    const d = changesSinceReturn(events);
    return d && d.answers ? (d.answers.before || {}) : null;
  }, [events]);

  const historyFmt = { changeLabels: QC_CHANGE_LABELS, fmtChangeValue: fmtQcHistoryValue };

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (review?.canRejectOnly && request.status === 'requested');
  // 驗收表的答案是一整包，逐題意見要對得起逐題差異（qcQuestions）。
  const expandQcChanges = useCallback(
    (c) => expandAnswerChanges(c, request?.qcType, { engagementNames: engagementNameMap(request?.engagements) }),
    [request?.qcType, request?.engagements],
  );
  // 驗收表的題目名字住在題庫裡，不在 QC_CHANGE_LABELS 裡——併進來，逐題意見
  // 才叫得出題目而不是 `answers.q_specialists`。
  const qcLabels = useMemo(
    () => ({ ...QC_CHANGE_LABELS, ...qcAnswerLabels(request?.qcType) }),
    [request?.qcType]
  );
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: qcLabels,
    expandChanges: expandQcChanges,
  });

  // A draft is not something to read — it is something to finish. The
  // requester lands straight in the form (Save draft / Submit / Delete).
  if (request && isDraft && canModify) {
    return (
      <WorkflowQcNew
        key={request.updatedAt || request.id}
        draft={request}
        onSaved={load}
        onSubmitted={load}
        onDeleted={() => navigate('/workflow/qc')}
      />
    );
  }

  return (
    <RequestDetailShell
      backTo="/workflow/qc"
      backLabel="QC6 / QC7 Forms"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel={request.qcType}
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            lock={engagementLock}
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && request.status === 'requested' ? approve : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify && request.status !== 'approved' ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            {request.status === 'approved' && (
              <div>
                <p className={detailLabel}>Result</p>
                <p className="text-sm text-green-600 mt-1">QC record created — engagement(s) active</p>
                {/* The approved form, with the sign-off block, as a PDF. */}
                <a
                  href={`${API_BASE_URL}/api/workflow/qc/${request.id}/pdf`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Download className="w-4 h-4" /> Download {request.qcType} Form (PDF)
                </a>
              </div>
            )}
            {review?.waitingForPriorStage && request.status === 'requested' && (
              <div>
                <p className={detailLabel}>Your Turn</p>
                <p className="text-sm text-slate-600 mt-1">
                  Your stage opens once the reviewer(s) before you have approved.
                </p>
              </div>
            )}
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            {...historyFmt}
            expandChanges={expandQcChanges}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          <SectionCard title={QC_TYPE_LABEL[request.qcType] || request.qcType}>
            <DetailGrid>
              {/* Same comment key as the Modify modal's coverage picker, so a
                  comment written here greets the requester on the control that
                  fixes it. */}
              <DetailItem label={`Engagement(s) Covered (${(request.engagements || []).length})`} field="engagements">
                <EngagementNameList items={request.engagements || []} keyOf={(e, i) => e.id || i} />
              </DetailItem>
              <DetailItem label="Client Group" field="clientGroup"><NowName value={request.clientGroup} now={request.clientGroupNow} /></DetailItem>
              {request.qcType === 'QC7' && (
                <DetailItem label="Risk Rating Changed from Prior Year" field="riskRatingChanged">
                  {request.riskRatingChanged == null ? '—' : (request.riskRatingChanged ? 'Yes (from the form)' : 'No (from the form)')}
                </DetailItem>
              )}
              <DetailItem label={request.additionalReviewerName ? 'First Reviewer' : 'Reviewer'} field="reviewerName">
                <NowName value={request.reviewerName} now={request.reviewerNameNow} />
              </DetailItem>
              {request.additionalReviewerName && (
                <DetailItem label="Second Reviewer" field="additionalReviewerName"><NowName value={request.additionalReviewerName} now={request.additionalReviewerNameNow} /></DetailItem>
              )}
              {request.thirdReviewerName && (
                <DetailItem label="Third Reviewer" field="thirdReviewerName"><NowName value={request.thirdReviewerName} now={request.thirdReviewerNameNow} /></DetailItem>
              )}
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          {request.answers && (
            <SectionCard title="QC6 / QC7 Form">
              {/* The same layout the submitter filled in. A struck-through
                  previous answer appears when the requester changed it after
                  a return; the per-field comment anchor keys on
                  `answers.<id>` — the History diff uses the same key. */}
              <QcFormView
                qcType={request.qcType}
                answers={request.answers}
                engagements={request.engagements || []}
                previousAnswers={answersWhenReturned}
                extra={(f) => (
                  <FieldCommentAnchor field={`answers.${f.id}`} label={f.label} className="mt-0.5" compact hoverOnly />
                )}
              />
            </SectionCard>
          )}

          <SectionCard title="Attachments">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/qc/${request.id}/files/${f.id}`}
            />
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          <HistoryCard
            events={events}
            {...historyFmt}
            expandChanges={expandQcChanges}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/qc/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <QcModifyModal request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the request back for review.'
                  : 'Changes saved. Recorded approvals were voided — the partners must approve again.');
                load();
              }} />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the request and its files. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => { await fetchJSON(`/api/workflow/qc/${request.id}`, { method: 'DELETE' }); navigate('/workflow/qc'); }} />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

const QcModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [engagements, setEngagements] = useState([]);
  const [engagementIds, setEngagementIds] = useState(
    (request.engagements || []).map((e) => e.engagementId).filter(Boolean)
  );
  const [answers, setAnswers] = useState(request.answers || {});
  const [files, setFiles] = useState([]);
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [picks, setPicks] = useState(() => reviewerPicksOf(request));
  const plan = useMemo(() => planFor(answers, request.qcType), [answers, request.qcType]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { removeIds, toggle: toggleRemove, appendTo } = useRemovableFiles();

  useEffect(() => {
    (async () => {
      try {
        // requestId puts this request's own coverage back into the feed in
        // full slim-list shape (the derived-answer lock reads the client
        // profile off it); the snapshot fallback below only serves legacy
        // rows whose engagement no longer exists.
        const data = await fetchJSON(`/api/workflow/qc/options?requestId=${request.id}`);
        const own = (request.engagements || [])
          .filter((e) => e.engagementId && !(data.engagements || []).some((x) => x.id === e.engagementId))
          .map((e) => ({
            id: e.engagementId, engagementName: e.engagementName, entityName: e.entityName, yearEnd: e.yearEnd,
            serviceProvided: e.serviceProvided, engagementSeq: e.engagementSeq, serviceDescription: e.serviceDescription,
            sourceRequestNo: null, partners: [],
          }));
        setEngagements([...(data.engagements || []), ...own]);
      } catch (e) { setError(e.message); }
    })();
  }, [request]);

  const selected = useMemo(
    () => engagements.filter((e) => engagementIds.includes(e.id)),
    [engagements, engagementIds]
  );

  // Everything still empty, keyed to the control it belongs to (the same keys
  // the return-comment anchors use) — after the first Save the modal marks
  // those controls and lists them in the banner. The questionnaire rows mark
  // themselves from the `answers.<id>` keys.
  const gaps = useMemo(() => {
    const g = [];
    if (engagementIds.length === 0) g.push({ key: 'engagements', label: 'Engagement(s) Covered' });
    g.push(...qcAnswerGaps(answers, request.qcType), ...qcSplitGaps(answers, request.qcType, engagementIds, { requireFee: true }));
    g.push(...reviewerPickGaps(plan, picks));
    return g;
  }, [engagementIds, request.qcType, plan, picks, answers]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      const form = new FormData();
      form.set('engagementIds', JSON.stringify(engagementIds));
      appendReviewerPicks(form, plan, picks);
      form.set('answers', JSON.stringify(answers));
      form.set('remarks', remarks);
      files.forEach(f => form.append('files', f));
      appendTo(form);
      await fetchJSON(`/api/workflow/qc/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ReturnCommentsBanner events={events} />
        {/* QC type 是這張單的身分，不是可以填的欄位（改型別＝另開一張單）。
            不能長得跟下面每一個輸入欄一樣（一行細字標題配一行同樣細的值，
            標示不清楚），所以做成一枚有邊框的唯讀
            晶片：值用粗體、旁邊接全名，鎖頭圖示替代解釋文字
            ——同一件事用一個圖示講完，不佔一行。 */}
        <Field label="QC Type">
          <LockTip text="Locked — a different type means a new request">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
              <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-sm font-semibold text-slate-900">{request.qcType}</span>
              <span className="text-sm text-slate-500">{qcTypeDescription(request.qcType)}</span>
            </div>
          </LockTip>
        </Field>
        <Field label="Engagement(s) Covered" required field="engagements">
          <GroupedEngagementPicker
            engagements={engagements}
            selectedIds={engagementIds}
            onChange={setEngagementIds}
          />
        </Field>
        <EditableAttachments
          files={request.files || []}
          removeIds={removeIds}
          onToggle={toggleRemove}
          downloadHref={(f) => `${API_BASE_URL}/api/workflow/qc/${request.id}/files/${f.id}`}
        />
        <FieldCommentInline field="files" />
        <QcAnswersForm
          qcType={request.qcType}
          answers={answers}
          onChange={setAnswers}
          engagements={selected}
          feePerEngagement
        />
        <Field label="Add More Supporting Files">
          <FilePicker multiple kind="docsAndImages" onChange={e => setFiles(Array.from(e.target.files || []))} />
        </Field>
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
        </Field>
        {/* Reviewer picks — last, matching the New page's final Reviewer
            section; the plan follows the answers as edited above. */}
        <QcReviewerPicks
          plan={plan}
          picks={picks}
          onChange={setPicks}
          qcType={request.qcType}
          hint="You pick who reviews this request, from the firm's roster. Changing it voids recorded approvals."
        />
        {(error || validation.error) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <span>{error || validation.error}</span>
          </div>
        )}
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowQcDetail;
